import Field from '@/components/common/Field/Field';
import * as S from './style';
import InputText from '@/components/common/Input/InputText/InputText';
import { useEffect } from 'react';
import InputSelect from '@/components/common/Input/InputSelect/InputSelect';
import { degreeOptions, formattedCountryOptions } from '@/constants/option';
import AddIcon from '@/components/Icon/AddIcon';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@/components/Icon/CloseIcon';
import { useGetMemberEduInfoApi, usePostModifyMemberEduInfoApi } from '@/api/mypage';
import { useToastStore } from '@/store/toastStore';
interface RegisterField {
	educations: { school: string; major: string; degree: string; year: string; country: string }[]; // "name"을 필드로 갖는 배열
}
const EducationInfo = () => {
	const { openToast } = useToastStore();
	const userRegisterSchema = z.object({
		educations: z.array(
			z.object({
				school: z.string().min(1, '학교를를 입력해 주세요.'),
				major: z.string().min(1, '전공을을 입력해 주세요.'),
				degree: z.string().min(1, '학위를 선택해 주세요.'),
				year: z.string().min(1, '졸업년도를 입력해 주세요.'),
				country: z.string().min(1, '국가를 선택해 주세요.'),
			})
		),
	});
	const {
		control,
		watch,
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<RegisterField>({
		resolver: zodResolver(userRegisterSchema),
		mode: 'onChange',
		defaultValues: {
			educations: [{ school: '', major: '', degree: '', year: '', country: '대한민국' }], // 초기값을 배열로 설정
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'educations', // ✅ 배열 필드명을 올바르게 사용
	});
	const { data: eduInfo } = useGetMemberEduInfoApi();
	const { mutateAsync: modifyEduInfo } = usePostModifyMemberEduInfoApi();
	const onValidateData = (_: RegisterField) => {
		append({ school: '', major: '', degree: '', year: '', country: '대한민국' });
	};
	const onSubmit = async (data: RegisterField) => {
		const res = await modifyEduInfo(
			data.educations.map((item) => ({
				member_edu_school: item.school,
				member_edu_specialty: item.major,
				member_edu_degree: item.degree,
				member_edu_gradation_year: item.year,
				member_edu_nation: item.country,
			}))
		);
		if (res.data.success) {
			openToast({
				subContent: '한국노화학회',
				content: '정보가 변경되었습니다.',
			});
		}
	};
	useEffect(() => {
		if (eduInfo) {
			const newEducations = eduInfo.map((item) => ({
				school: item.member_edu_school,
				major: item.member_edu_specialty,
				degree: item.member_edu_degree,
				year: item.member_edu_gradation_year,
				country: item.member_edu_nation,
			}));

			// 첫 번째 데이터는 setValue로 직접 설정
			if (newEducations.length > 0) {
				setValue('educations', newEducations);
			}
		}
	}, [eduInfo, setValue]);

	return (
		<S.FormContainer onSubmit={handleSubmit(onSubmit)}>
			<S.Container>
				{fields.map((f, index) => (
					<S.FieldBox key={f.id}>
						<S.Title>
							학력정보 {index + 1 > 9 ? '' : '0'}
							{index + 1}
							{index + 1 > 1 && <CloseIcon onClose={() => remove(index)} />}
						</S.Title>
						<S.FlexRow>
							<Field title="출신학교" error={errors.educations?.[index]?.school?.message ?? ''}>
								<InputText
									isError={!!errors.educations?.[index]?.school?.message}
									value={watch(`educations.${index}.school`)}
									input={(e) => setValue(`educations.${index}.school`, e.target.value, { shouldValidate: true })}
									placeholder="학교 입력"
								/>
							</Field>
							<Field title="전공" error={errors.educations?.[index]?.major?.message ?? ''}>
								<InputText
									isError={!!errors.educations?.[index]?.major?.message}
									value={watch(`educations.${index}.major`)}
									input={(e) => setValue(`educations.${index}.major`, e.target.value, { shouldValidate: true })}
									placeholder="전공 입력"
								/>
							</Field>
						</S.FlexRow>
						<Field title="학위" error={errors.educations?.[index]?.degree?.message ?? ''}>
							<S.SelectBox>
								<InputSelect
									defaultValue="선택"
									options={degreeOptions}
									selectedOption={watch(`educations.${index}.degree`)}
									setSelectedOption={(value) => setValue(`educations.${index}.degree`, value, { shouldValidate: true })} // 학위 선택
								/>
							</S.SelectBox>
						</Field>
						<Field title="졸업년도" error={errors.educations?.[index]?.year?.message ?? ''}>
							<S.SelectBox>
								<InputText
									isError={!!errors.educations?.[index]?.year?.message}
									value={watch(`educations.${index}.year`)}
									input={(e) => {
										e.target.value = e.target.value.replace(/[^0-9]/g, ''); // 숫자만 허용
										setValue(`educations.${index}.year`, e.target.value, { shouldValidate: true });
									}}
									placeholder="졸업년도 입력"
								/>
							</S.SelectBox>
						</Field>
						<Field title="국가" error={errors.educations?.[index]?.country?.message ?? ''}>
							<S.SelectBox>
								<InputSelect
									options={formattedCountryOptions}
									selectedOption={watch(`educations.${index}.country`)}
									setSelectedOption={(value) => setValue(`educations.${index}.country`, value, { shouldValidate: true })} // 국가 선택
								/>
							</S.SelectBox>
						</Field>
					</S.FieldBox>
				))}
				<S.AddButton type="button" onClick={handleSubmit(onValidateData)}>
					<AddIcon />
					추가
				</S.AddButton>
			</S.Container>
			<S.NextButton type="submit">변경완료</S.NextButton>
		</S.FormContainer>
	);
};
export default EducationInfo;
